import { useEffect, useState } from 'react'
import { fetchScrollstory } from './data/prismic'
import ScrollOverMedia from './components/scrollOverMedia/ScrollOverMedia'
import {
  ScrollstoryDocument,
  ScrollstoryDocumentDataBodySubdocumentRandomizedSliceItem,
  ScrollstoryDocumentDataBodySubdocumentSlicePrimary,
} from '../types.generated'
import { isFilledContentRelationshipField } from './data/prismic-helper'
import InlineImage from './components/inline/InlineImage'
import InlineText from './components/inline/InlineText'
import InlineVideo from './components/inline/InlineVideo'
import { useIsPortrait } from './util/breakpoints'
import { InlineFoldout } from './components/inline/InlineFoldout.tsx'
import { InlineQuote } from './components/inline/InlineQuote.tsx'
import { InlineInfogram } from './components/inline/InlineInfogram.tsx'
import styled from '@emotion/styled'
import { asText } from '@prismicio/client'

export interface ScrollstoryProps {
  lang: string
  scrollstoryid: string
}

const ScrollstoryContainer = styled.div<{ css: string }>`
  ${({ css }) => css}
`

function Scrollstory(props: ScrollstoryProps) {
  const [scrollstory, setScrollstory] = useState<ScrollstoryDocument>()
  const isPortrait = useIsPortrait()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const scrollstoryData = await fetchScrollstory(
          props.scrollstoryid,
          props.lang
        )
        setScrollstory(scrollstoryData)
      } catch (error) {
        console.error(
          `Scrollstory with id ${props.scrollstoryid} not found in lang ${props.lang}`
        )
      }
    }

    fetchData()
  }, [props.scrollstoryid, props.lang])

  const isScrollOverMediaSection = (sliceType: string | undefined): boolean =>
    sliceType === 'subdocument' || sliceType === 'subdocument_randomized'

  const nextSectionIsScrollOverMediaOrEnd = (sectionIndex: number): boolean => {
    const nextSection = scrollstory?.data.body[sectionIndex + 1]

    return (
      isScrollOverMediaSection(nextSection?.slice_type) ||
      nextSection === undefined
    )
  }

  const renderSubdocument = (
    subdocumentData:
      | ScrollstoryDocumentDataBodySubdocumentSlicePrimary
      | ScrollstoryDocumentDataBodySubdocumentRandomizedSliceItem,
    sectionIndex: number
  ): JSX.Element => {
    let document
    if (isPortrait) {
      document = subdocumentData.subdocument_portrait
    } else {
      document = subdocumentData.subdocument
    }

    if (!isFilledContentRelationshipField(document)) {
      console.error(
        `Scrollstory contained a subdocument link, but no scroll_over_media was linked to current responsive mode. Is portrait: ${isPortrait}. Will ignore the document. Document: ${JSON.stringify(
          subdocumentData
        )}`
      )
      return <div key={sectionIndex}></div>
    }

    if (document.type === 'scroll_over_media') {
      return (
        <ScrollOverMedia
          id={document.id}
          lang={props.lang}
          sectionIndex={sectionIndex}
          key={sectionIndex}
          // We don't want margins between scroll over media documents or subdocument_randomized sections or at the end of the story
          disableBottomMargin={nextSectionIsScrollOverMediaOrEnd(sectionIndex)}
        />
      )
    } else {
      console.error(
        `Subdocument type '${document.type}' is not known. Ignoring...`
      )
      return <div key={sectionIndex}></div>
    }
  }

  const scrollstoryContainerCss =
    asText(scrollstory?.data.scrollstory_container_css) ?? ''

  return (
    <>
      {scrollstory && (
        <ScrollstoryContainer css={scrollstoryContainerCss}>
          {scrollstory.data.body.map((section, sectionIndex) => {
            if (section.slice_type === 'article') {
              return (
                <InlineText text={section.primary.text} key={sectionIndex} />
              )
            }

            if (section.slice_type === 'image') {
              return <InlineImage image={section.primary} key={sectionIndex} />
            }

            if (section.slice_type === 'fold-out') {
              return (
                <InlineFoldout
                  data={section.primary}
                  key={sectionIndex}
                  lang={props.lang}
                />
              )
            }

            if (section.slice_type === 'video') {
              return (
                <InlineVideo
                  video={section.primary}
                  key={sectionIndex}
                  playback={section.primary.playback}
                  lang={props.lang}
                />
              )
            }

            if (section.slice_type === 'quote') {
              return <InlineQuote quote={section.primary} key={sectionIndex} />
            }

            if (section.slice_type === 'infogram') {
              const { infogram_id, infogram_type, infogram_title } =
                section.primary
              return (
                <InlineInfogram
                  id={infogram_id ?? ''}
                  type={infogram_type ?? ''}
                  title={infogram_title ?? ''}
                  key={sectionIndex}
                />
              )
            }

            if (section.slice_type === 'subdocument') {
              return renderSubdocument(section.primary, sectionIndex)
            }

            if (section.slice_type === 'subdocument_randomized') {
              const items = section.items
              if (!items || items.length === 0) {
                console.error(
                  'Link to subdocument (randomized) has no items. Ignoring...'
                )
                return <div key={sectionIndex}></div>
              }

              const randomIndex = Math.floor(Math.random() * items.length)
              const randomItem = items[randomIndex]
              return renderSubdocument(randomItem, sectionIndex)
            } else {
              // TypeScript thinks section has type 'never', since we catched all slice_types,
              // making this unreachable. But it can still happen when types are updated in
              // Prismic, but not yet in the code.
              const sliceType = (section as { slice_type: unknown })?.slice_type
              console.error(
                `Slice type '${JSON.stringify(sliceType)}' is not known. Ignoring...`
              )
              return <div key={sectionIndex}></div>
            }
          })}
        </ScrollstoryContainer>
      )}
    </>
  )
}

export default Scrollstory
